import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MoveHorizontal } from "lucide-react";
import {
  PROJECTS,
  IMG_INTERIOR_VIEW,
  waLink,
} from "../lib/data";

function BeforeAfterSlider({ image, beforeLabel, afterLabel }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 select-none bg-[#0A0B0E]"
    >
      {/* DESPUÉS — con película aplicada: reduce deslumbre y mejora la vista (más contraste, cielo más definido, sin oscurecer demasiado) */}
      <img
        src={image}
        alt="Vista al exterior CON película de polarizado aplicada"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          objectPosition: "50% 40%",
          filter:
            "brightness(0.92) contrast(1.18) saturate(1.18)",
        }}
        draggable="false"
      />
      {/* Sutil tinte azulado/control solar — apenas perceptible */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(120,150,180,0.08) 0%, rgba(80,110,140,0.05) 100%)",
        }}
      />

      {/* ANTES — vidrio claro sin película (imagen original, sin filtros, con brillo) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={image}
          alt="Vista al exterior SIN película — vidrio claro deslumbrante"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: "50% 40%",
            filter: "brightness(1.22) saturate(0.85) contrast(0.92)",
          }}
          draggable="false"
        />
        {/* Glare/sol directo — efecto deslumbrante del vidrio sin película */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen"
          style={{
            background:
              "radial-gradient(ellipse at 65% 25%, rgba(255,245,210,0.55), rgba(255,250,230,0.2) 35%, transparent 65%)",
          }}
        />
        {/* Velo blanco para simular vidrio sin tratar bajo sol directo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "rgba(255,250,235,0.08)",
          }}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-5 left-5 text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border border-white/20 bg-black/55 backdrop-blur-xl text-white z-10">
        Antes · {beforeLabel}
      </div>
      <div className="absolute top-5 right-5 text-[11px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/15 backdrop-blur-xl text-[#D4AF37] z-10">
        Después · {afterLabel}
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-[#D4AF37] pointer-events-none z-10"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.6)]">
          <MoveHorizontal size={20} className="text-black" />
        </div>
      </div>

      {/* Range input overlay */}
      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(parseInt(e.target.value))}
        data-testid="before-after-range"
        aria-label="Arrastrar para comparar antes y después"
        className="ba-range absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20"
      />
    </div>
  );
}

export default function Gallery() {
  return (
    <section
      id="proyectos"
      data-testid="gallery-section"
      className="relative bg-[#0A0B0E] py-24 lg:py-36 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-8 h-px bg-[#D4AF37]" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-[0.3em]">
                Trabajos realizados
              </span>
            </div>
            <h2 className="font-display text-4xl lg:text-6xl leading-[1.05] tracking-tight text-white">
              Proyectos{" "}
              <span className="italic text-[#D4AF37]">residenciales e industriales</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base lg:text-lg text-white/55 font-light leading-relaxed">
              Cada instalación refleja nuestro compromiso con la calidad.
              Películas Ray-Ban, inteligente azul, seguridad humo, esmerilada y
              control solar — en residencias, comercios, oficinas y edificios.
            </p>
          </div>
        </div>

        {/* Before / After hero slider */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-6"
        >
          <BeforeAfterSlider
            image={IMG_INTERIOR_VIEW}
            beforeLabel="Sin película · deslumbre y calor"
            afterLabel="Con polarizado · vista confortable"
          />
          <div className="mt-4 text-xs uppercase tracking-[0.25em] text-white/40 flex items-center gap-3">
            <MoveHorizontal size={14} className="text-[#D4AF37]" />
            Arrastra el control deslizante para comparar
          </div>
        </motion.div>

        {/* Project grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECTS.map((p, i) => (
            <motion.a
              key={p.id}
              href={waLink(`Hola, vi el proyecto "${p.title}" y me gustaría algo similar`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`project-card-${p.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/8 bg-[#14161C] aspect-[4/5] ${
                i === 1 ? "md:row-span-2 lg:aspect-auto" : ""
              }`}
            >
              <img
                src={p.image}
                alt={p.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1.5s]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/30 to-transparent" />

              <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full border border-white/20 bg-black/40 backdrop-blur-xl text-white">
                {p.category}
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-7">
                <h3 className="font-display text-2xl lg:text-3xl text-white tracking-tight mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-white/60 font-light leading-relaxed">
                  {p.desc}
                </p>
                <div className="mt-4 flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity">
                  Ver más como este
                  <ArrowUpRight size={14} />
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        <div className="mt-14 text-center">
          <a
            href={waLink("Hola, me gustaría un resultado como los de su galería")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-whatsapp-cta"
            className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:shadow-[0_0_40px_rgba(212,175,55,0.45)] transition-all"
          >
            Quiero un resultado así — Cotizar ahora
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
